import ConfirmationModal from "components/ConfirmationModal";
import SideModal from "components/SideModal";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { ChangeEvent, useRef, useState } from "react";
import {
  addCouncilLitigation,
  addLitigation,
  editComplaint,
} from "services/prosecutorDecipilineService";
import { Complaints, Litigation, Summon } from "types";
import { LoadingIcon } from "utils/constant";
import { APPOINTMENT_REASON, COMPLIANT_STATUS } from "utils/constantValues";
import { ROLES } from "utils/roles";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: Summon;
  complaintData: Complaints;
  onLitigationRefresh: any;
}

export default function AddDailyHearingModal({
  isOpen,
  setIsOpen,
  summonData,
  complaintData,
  onLitigationRefresh,
}: ModalProps) {
  const { t } = useTranslation();
  const canCreateInspectionDailyHearing = useHasRole([
    ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canCreateCouncilDailyHearing = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);

  const { showToast } = useToast();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);

  const [formState, setFormState] = useState<Litigation>({
    is_final_verdict_given: false,
    hearing_date: "",
    next_appointment_date: "",
    given_order: "",
    appointment_reason: "",
    daily_report_summary: "",
    summon: null,
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles([...Array.from(e.target.files as FileList)]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!summonData) {
      showToast(t("summonInformationNotFound"), "error");
      return;
    }

    // Check if this is a final decision
    if (formState.appointment_reason === APPOINTMENT_REASON.FINAL_DECISION) {
      setShowConfirmationModal(true);
      return;
    }

    await submitHearingData();
  };

  const submitHearingData = async () => {
    setLoading(true);

    const data = new FormData();
    data.append("hearing_date", formState.hearing_date);
    data.append("next_appointment_date", formState.next_appointment_date);
    data.append("given_order", formState.given_order || "");
    data.append("appointment_reason", formState.appointment_reason);
    data.append("daily_report_summary", formState.daily_report_summary);
    data.append("summon", summonData?.id || "");

    if (files.length > 0) {
      data.append("meeting_files", files[0]);
    }

    data.append(
      "assigned_prosecutor",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "accused_prosecutor_name",
      complaintData?.accused_prosecutor_name || "",
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "accused_prosecutor_org_id",
      complaintData?.accused_prosecutor_org_id || "",
    );
    data.append(
      "accused_prosecutor_admin_unit_id",
      complaintData?.accused_prosecutor_admin_unit_id || "",
    );
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append(
      "is_final_verdict_given",
      (
        formState.appointment_reason === APPOINTMENT_REASON.FINAL_DECISION
      ).toString(),
    );

    const isFormData = true;

    try {
      const api = canCreateInspectionDailyHearing
        ? addLitigation
        : canCreateCouncilDailyHearing
          ? addCouncilLitigation
          : null;

      if (!api) {
        showToast(t("noPermissionToCreateDailyHearing"), "error");
        return;
      }

      const resData = await api(data, isFormData);

      if (resData) {
        await editComplaint({
          ...complaintData,
          status: COMPLIANT_STATUS.UNDER_LITIGATION,
        });
      }

      await Promise.resolve(onLitigationRefresh?.());
      setIsOpen(false);
      showConfirmationModal && setShowConfirmationModal(false);
      showToast(t("dailyHearingOfficiallyDocumented"), "success");

      // Reset form
      setFiles([]);
      setFormState({
        is_final_verdict_given: false,
        hearing_date: "",
        next_appointment_date: "",
        given_order: "",
        appointment_reason: "",
        daily_report_summary: "",
        summon: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("failedToDocumentDailyHearing"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const getModalTitle = () => {
    if (canCreateInspectionDailyHearing) {
      return t("inspectionOfficeDailyHearing");
    } else if (canCreateCouncilDailyHearing) {
      return t("councilOfficeDailyHearing");
    }
    return t("dailyHearingRecord");
  };

  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={getModalTitle()}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Hearing Details Section */}
        <section className="space-y-4">
          <div className="flex items-center">
            <div className="h-5 w-1 bg-red-700 rounded-r mr-3"></div>
            <h3 className="text-base font-semibold text-gray-800">
              {t("hearingDetails")}
            </h3>
          </div>

          <div className="space-y-4 pl-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("hearingDate")} <span className="text-red-600">*</span>
              </label>
              <input
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                type="date"
                name="hearing_date"
                value={formState.hearing_date}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("nextAppointmentDate")}{" "}
                <span className="text-red-600">*</span>
              </label>
              <input
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                type="date"
                name="next_appointment_date"
                value={formState.next_appointment_date}
                onChange={handleChange}
                min={
                  formState.hearing_date
                    ? new Date(
                        new Date(formState.hearing_date).getTime() + 86400000,
                      )
                        .toISOString()
                        .split("T")[0]
                    : new Date().toISOString().split("T")[0]
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                {t("mustBeAfterTheHearingDate")}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("councilOrderInstruction")}
              </label>
              <textarea
                rows={3}
                name="given_order"
                value={formState.given_order}
                onChange={handleChange}
                placeholder={t("enterOfficialCouncilOrdersOrInstructions")}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-y"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("appointmentReason")} <span className="text-red-600">*</span>
              </label>
              <select
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="appointment_reason"
                value={formState.appointment_reason ?? ""}
                onChange={handleChange}
              >
                <option value="">{t("selectAppointmentReason")}</option>
                {Object.values(APPOINTMENT_REASON)?.map((list) => (
                  <option key={list} value={list}>
                    {list}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {formState.appointment_reason ===
                  APPOINTMENT_REASON.FINAL_DECISION &&
                  t("finalHearingDecisionWarning")}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("dailyReportSummary")}{" "}
                <span className="text-red-600">*</span>
              </label>
              <textarea
                required
                rows={4}
                name="daily_report_summary"
                value={formState.daily_report_summary}
                onChange={handleChange}
                placeholder={t("provideOfficialSummaryOfTodayProceedings")}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-y"
              />
            </div>
          </div>
        </section>

        {/* Meeting Minutes Section */}
        {canCreateCouncilDailyHearing && (
          <section className="space-y-4">
            <div className="flex items-center">
              <div className="h-5 w-1 bg-red-700 rounded-r mr-3"></div>
              <h3 className="text-base font-semibold text-gray-800">
                {t("minuteDocumentation")}
              </h3>
            </div>

            <div className="pl-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("uploadMeetingMinutes")}
              </label>

              {/* File Upload Area */}
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-400 hover:bg-red-50 transition-colors cursor-pointer"
                onClick={triggerFileInput}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                />
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mb-3">
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {t("uploadOfficialMinutes")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("fileTypesWithMax10mb")}
                  </p>
                </div>
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    {t("selectedFiles")} ({files.length})
                  </p>
                  <div className="space-y-2">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2.5"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded">
                            <span className="text-xs font-medium text-red-700">
                              {file.type
                                .split("/")[1]
                                ?.substring(0, 3)
                                .toUpperCase() || "DOC"}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800 truncate max-w-xs">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(file.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Action Buttons */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                !formState.hearing_date ||
                !formState.next_appointment_date ||
                !formState.appointment_reason ||
                !formState.daily_report_summary
              }
              className={`flex-1 px-4 py-2.5 text-white text-sm font-medium rounded-md transition-colors flex items-center justify-center space-x-2 ${
                loading ||
                !formState.hearing_date ||
                !formState.next_appointment_date ||
                !formState.appointment_reason ||
                !formState.daily_report_summary
                  ? "bg-red-400 cursor-not-allowed"
                  : "bg-red-700 hover:bg-red-800"
              }`}
            >
              {loading ? (
                <>
                  {LoadingIcon}
                  <span>{t("processing")}</span>
                </>
              ) : (
                t("documentHearing")
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-3">
            {t("dailyHearingRecordWillBeOfficiallyDocumented")}
          </p>
        </div>
      </form>

      {/* Final Decision Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={submitHearingData}
        isLoading={loading}
        title={t("finalHearingDecisionConfirmation")}
        message={t("finalHearingDecisionConfirmationMessage")}
        confirmText={t("recordFinalDecision")}
        cancelText={t("goBack")}
      />
    </SideModal>
  );
}
